// SPDX-License-Identifier: Apache-2.0

// /dev/orbit — kernel diagnostic visualization (was /orbit). Moved here on
// 2026-04-26 so the kernel benches stop masquerading as the product. Page
// content unchanged from the original Orbit.tsx; only addition is the
// DevBanner and the relative import paths got one extra ../ because we live
// under routes/dev/.
//
// HIGHEST-RISK DECISION (documented in PR / agent return):
//   We bind pos_out → renderer via the *copy-out fallback*, NOT TSL `storage()`.
//   Reason: r171's TSL `storage()` node is well-documented for *render* pipelines but
//   wiring it to an InstancedMesh transform attribute requires the WebGPURenderer's
//   internal Node graph to share the very GPUBuffer we're writing to from a hand-rolled
//   compute pass running off the same device. We tried it; the binding shows the buffer
//   contents only after the next frame's render flush, and inter-encoder ordering
//   surfaced as a one-frame lag plus intermittent stale reads on M-class adapters.
//
//   The fallback is a pipelined async readback: each frame we copyBufferToBuffer
//   pos_out → a ring of staging buffers, mapAsync() on the next-oldest, and write the
//   freshly-mapped Float32Array into an InstancedMesh's per-instance offset attribute.
//   Net cost at N=5k: 80 KB / frame readback over the unified-memory bus on M-class —
//   sub-100 µs, well inside the frame budget. The render thread NEVER awaits the
//   readback synchronously; the InstancedMesh just renders whatever positions the most
//   recent unmap delivered (stale by 1–2 frames, invisible at 60 Hz).

import { onCleanup, onMount, createSignal, Show } from "solid-js";
import * as THREE from "three";
import { createDevice } from "@tether/kernels";
// Relative import into the kernels package — packages/kernels/index.ts is owned by
// Athena and will get the forceLayout re-exports added in a follow-up pass. Until then,
// we reach into the package's source file directly so the page is self-sufficient.
// NOTE: extra ../ vs the original Orbit.tsx because we live under routes/dev/.
import {
  setupForceLayout,
  stepForceLayout,
  disposeForceLayout,
  type ForceEdge,
  type ForceLayoutSetup,
} from "../../../../../packages/kernels/forceLayoutStep";

const NODE_BYTES = 16; // vec4 stride

interface Stats {
  n: number;
  fps: number;
  stepMs: number;
  edges: number;
}

export default function DevOrbit() {
  let canvasRef: HTMLCanvasElement | undefined;
  const [error, setError] = createSignal<string | null>(null);
  const [stats, setStats] = createSignal<Stats | null>(null);
  const [n, setN] = createSignal(5_000);
  const [running, setRunning] = createSignal(false);

  let cleanup: (() => void) | null = null;

  async function start(targetN: number) {
    setError(null);
    setStats(null);
    if (cleanup) {
      cleanup();
      cleanup = null;
    }
    if (!canvasRef) {
      setError("canvas ref missing");
      return;
    }
    setRunning(true);
    try {
      cleanup = await launch(canvasRef, targetN, setStats);
    } catch (e) {
      setError(String(e instanceof Error ? `${e.message}\n${e.stack ?? ""}` : e));
      setRunning(false);
    }
  }

  onMount(() => {
    void start(n());
  });

  onCleanup(() => {
    if (cleanup) cleanup();
  });

  return (
    <>
      <div class="dev-banner">DEV — kernel diagnostic page — internal use</div>
      <div style={{ padding: "20px", "max-width": "1080px", margin: "0 auto", "font-family": "system-ui, sans-serif" }}>
        <h1 style={{ margin: "0 0 8px 0", "font-size": "24px", "letter-spacing": "-0.02em" }}>
          forceLayoutStep · orbit
        </h1>
        <p style={{ color: "#9a9ab0", "margin-top": 0, "font-size": "14px" }}>
          Kernel 3 of 5 from <code>kernels/MIGRATION.md</code>. N-body force sim runs every frame
          on the GPU; positions stream back via a ring of async-mapped staging buffers (no
          per-frame stall on the render thread).
        </p>

        <div style={{ display: "flex", gap: "8px", "flex-wrap": "wrap", margin: "16px 0" }}>
          <button
            style={btn}
            disabled={running() && n() === 5_000}
            onClick={() => {
              setN(5_000);
              void start(5_000);
            }}
          >5k nodes</button>
          <button
            style={btn}
            disabled={running() && n() === 10_000}
            onClick={() => {
              setN(10_000);
              void start(10_000);
            }}
          >10k nodes</button>
          <button
            style={btn}
            disabled={running() && n() === 25_000}
            onClick={() => {
              setN(25_000);
              void start(25_000);
            }}
          >25k nodes</button>
          <button
            style={btn}
            disabled={running() && n() === 50_000}
            onClick={() => {
              setN(50_000);
              void start(50_000);
            }}
          >50k nodes</button>
        </div>

        <Show when={error()}>
          <pre style={{
            color: "#fca5a5",
            padding: "12px 16px",
            background: "rgba(252,165,165,0.08)",
            border: "1px solid rgba(252,165,165,0.3)",
            "border-radius": "8px",
            margin: "12px 0",
            "white-space": "pre-wrap",
            "font-size": "12px",
          }}>{error()}</pre>
        </Show>

        <div style={{ position: "relative", width: "100%", "aspect-ratio": "16 / 10", background: "#07070c", "border-radius": "12px", overflow: "hidden", border: "1px solid rgba(167,139,250,0.2)" }}>
          <canvas
            ref={(el) => { canvasRef = el; }}
            data-orbit-canvas
            style={{ width: "100%", height: "100%", display: "block" }}
          />
          <Show when={stats()}>
            {(s) => (
              <div data-orbit-stats style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                padding: "8px 12px",
                background: "rgba(7,7,12,0.75)",
                "backdrop-filter": "blur(8px)",
                "border-radius": "6px",
                "font-family": "ui-monospace, SF Mono, monospace",
                "font-size": "12px",
                color: "#d8d8e8",
                "line-height": 1.5,
              }}>
                <div>N: <span data-orbit-n>{s().n.toLocaleString()}</span></div>
                <div>fps: <span data-orbit-fps style={{ color: s().fps > 30 ? "#86efac" : "#fde047" }}>{s().fps.toFixed(1)}</span></div>
                <div>step: <code>{s().stepMs.toFixed(2)} ms</code></div>
                <div>edges: <code>{s().edges.toLocaleString()}</code></div>
              </div>
            )}
          </Show>
        </div>

        <p style={{ "margin-top": "16px", "font-size": "12px", color: "#6a6a80" }}>
          Constants port byte-for-byte from <code>vault-orbit.tsx:164–173</code>:
          K_REPEL=0.9, MIN_D2=0.35, K_SPRING=0.06, REST=2.2, DAMPING=0.86, MAX_V=6.0,
          CAP_DT=min(dt, 0.05). Springs use atomicAdd&lt;i32&gt; on q16.16 fixed-point
          because WebGPU has no atomicAdd&lt;f32&gt;.
        </p>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// launch — sets up renderer + force-sim + render loop. Returns a teardown fn.
// ----------------------------------------------------------------------------
async function launch(
  canvas: HTMLCanvasElement,
  N: number,
  setStats: (s: Stats) => void,
): Promise<() => void> {
  // 1. WebGPU device — we own it and pass it to three.js (so the Renderer doesn't
  //    request a fresh one and split bind-group lifetimes across two devices).
  const { device } = await createDevice();

  // 2. Synthesize an initial scatter + a small set of edges so springs do something.
  //    Layout: roughly spherical shell of radius 6, perturbed by random ε.
  const initial = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    const r = 4 + Math.random() * 2;
    initial[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
    initial[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    initial[i * 3 + 2] = r * Math.cos(phi);
  }

  // Edges: random ~1.5 N explicit + ~2 N semantic (within-cluster, weight 0.5).
  // Cap absolute count so 50k case stays under ~200k edges (kernel atomics scale fine).
  const explicitCount = Math.min(Math.floor(N * 1.5), 100_000);
  const semanticCount = Math.min(Math.floor(N * 2.0), 100_000);
  const edges: ForceEdge[] = [];
  for (let i = 0; i < explicitCount; i++) {
    let a = Math.floor(Math.random() * N);
    let b = Math.floor(Math.random() * N);
    if (a === b) b = (b + 1) % N;
    edges.push({ a, b, kind: 0 });
  }
  for (let i = 0; i < semanticCount; i++) {
    let a = Math.floor(Math.random() * N);
    let b = Math.floor(Math.random() * N);
    if (a === b) b = (b + 1) % N;
    edges.push({ a, b, kind: 1, w: 0.4 + Math.random() * 0.4 });
  }

  // 3. Build the force-sim setup.
  const setup: ForceLayoutSetup = await setupForceLayout(device, N, edges, initial);

  // 4. Set up three.js scene with a plain WebGLRenderer driving an InstancedMesh.
  //    We deliberately use WebGLRenderer here: the GPU buffer holding pos_out is on
  //    OUR GPUDevice, not the Renderer's, so even if we used WebGPURenderer we'd
  //    still have to copy positions across. WebGLRenderer is universally supported,
  //    keeps the dependency chain simple, and lets the user view the orbit on
  //    machines without a working WebGPURenderer init path. The compute kernel
  //    runs on real WebGPU regardless — that's what we're benching.
  // preserveDrawingBuffer:true so e2e tests (and any debugging) can drawImage()
  // the canvas onto a 2D context for pixel inspection. Costs a tiny per-frame
  // copy, fine for a dev/diagnostic page.
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  const updateSize = () => {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x07070c);
  const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 200);
  camera.position.set(0, 0, 18);
  camera.lookAt(0, 0, 0);

  // Ambient violet glow + central directional.
  scene.add(new THREE.AmbientLight(0x6a4dff, 0.5));
  const sun = new THREE.DirectionalLight(0xffffff, 0.6);
  sun.position.set(5, 10, 7);
  scene.add(sun);

  // InstancedMesh of small spheres. Per-instance position drives the matrix.
  const dotGeom = new THREE.SphereGeometry(0.06, 6, 6);
  const dotMat = new THREE.MeshStandardMaterial({ color: 0xa78bfa, emissive: 0x4d3aff, emissiveIntensity: 0.4, roughness: 0.5 });
  const instMesh = new THREE.InstancedMesh(dotGeom, dotMat, N);
  instMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  scene.add(instMesh);

  const tmpMat = new THREE.Matrix4();
  const tmpScale = new THREE.Vector3(1, 1, 1);
  const tmpQuat = new THREE.Quaternion();

  // 5. Async readback ring: 2 staging buffers, alternate which one we map each frame.
  const readbacks: GPUBuffer[] = [];
  const readbackBusy: boolean[] = [false, false];
  for (let i = 0; i < 2; i++) {
    readbacks.push(device.createBuffer({
      label: `orbit-readback-${i}`,
      size: N * NODE_BYTES,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    }));
  }
  // Cached CPU-side positions; updated whenever a readback resolves.
  const cpuPositions = new Float32Array(N * 4);
  // Seed with initial positions so the first frame draws something sensible.
  for (let i = 0; i < N; i++) {
    cpuPositions[i * 4 + 0] = initial[i * 3 + 0]!;
    cpuPositions[i * 4 + 1] = initial[i * 3 + 1]!;
    cpuPositions[i * 4 + 2] = initial[i * 3 + 2]!;
  }
  let readyForRender = true; // first paint can use the seed positions.

  function applyPositionsToInstancedMesh() {
    for (let i = 0; i < N; i++) {
      const x = cpuPositions[i * 4 + 0]!;
      const y = cpuPositions[i * 4 + 1]!;
      const z = cpuPositions[i * 4 + 2]!;
      tmpMat.compose(new THREE.Vector3(x, y, z), tmpQuat, tmpScale);
      instMesh.setMatrixAt(i, tmpMat);
    }
    instMesh.instanceMatrix.needsUpdate = true;
  }
  applyPositionsToInstancedMesh();

  // 6. The animation loop.
  let stopped = false;
  let lastTime = performance.now();
  let frameStart = performance.now();
  let fpsAccumFrames = 0;
  let fpsAccumStart = performance.now();
  let displayedFps = 0;
  let lastStepMs = 0;

  let nextReadback = 0;

  async function frame() {
    if (stopped) return;
    const t = performance.now();
    const dt = Math.min((t - lastTime) / 1000, 0.05);
    lastTime = t;
    frameStart = t;

    // (a) GPU step. We submit our own encoder (own error scope) so the renderer's
    //     encoder isn't entangled with a GPUDevice it doesn't own.
    const t0 = performance.now();
    try {
      await stepForceLayout(setup, dt);
    } catch (e) {
      console.error("[orbit] forceLayoutStep failed:", e);
      stopped = true;
      return;
    }

    // (b) Schedule a copy + mapAsync into one of the readback buffers if free.
    const slot = nextReadback;
    if (!readbackBusy[slot]) {
      readbackBusy[slot] = true;
      nextReadback = (nextReadback + 1) % readbacks.length;
      const enc = device.createCommandEncoder({ label: `orbit-readback-copy-${slot}` });
      enc.copyBufferToBuffer(setup.posOutBuf, 0, readbacks[slot]!, 0, N * NODE_BYTES);
      device.queue.submit([enc.finish()]);
      readbacks[slot]!.mapAsync(GPUMapMode.READ).then(() => {
        if (stopped) {
          try { readbacks[slot]!.unmap(); } catch { /* destroyed */ }
          return;
        }
        const view = new Float32Array(readbacks[slot]!.getMappedRange().slice(0));
        readbacks[slot]!.unmap();
        cpuPositions.set(view);
        readbackBusy[slot] = false;
        readyForRender = true;
      }).catch((err) => {
        console.warn("[orbit] readback mapAsync rejected:", err);
        readbackBusy[slot] = false;
      });
    }
    const t1 = performance.now();
    lastStepMs = t1 - t0;

    // (c) Render with whatever positions the most recent readback delivered.
    if (readyForRender) {
      applyPositionsToInstancedMesh();
    }
    updateSize();
    instMesh.rotation.y += dt * 0.05;
    renderer.render(scene, camera);

    // (d) FPS accounting (1-second window).
    fpsAccumFrames += 1;
    const tNow = performance.now();
    if (tNow - fpsAccumStart > 1000) {
      displayedFps = (fpsAccumFrames * 1000) / (tNow - fpsAccumStart);
      fpsAccumFrames = 0;
      fpsAccumStart = tNow;
      setStats({ n: N, fps: displayedFps, stepMs: lastStepMs, edges: edges.length });
    }

    requestAnimationFrame(() => void frame());
  }

  // Resize listener.
  const onResize = () => updateSize();
  window.addEventListener("resize", onResize);

  requestAnimationFrame(() => void frame());

  // Return teardown.
  return () => {
    stopped = true;
    window.removeEventListener("resize", onResize);
    try {
      disposeForceLayout(setup);
    } catch { /* swallow */ }
    for (const rb of readbacks) {
      try { rb.destroy(); } catch { /* may already be unmapped */ }
    }
    renderer.dispose();
    dotGeom.dispose();
    dotMat.dispose();
  };
}

const btn = {
  padding: "8px 16px",
  background: "rgba(167,139,250,0.12)",
  border: "1px solid rgba(167,139,250,0.4)",
  color: "#e8d8ff",
  "border-radius": "8px",
  cursor: "pointer",
  "font-size": "13px",
  "font-family": "inherit",
} as const;
